import { createContext, useContext, useState, ReactNode } from 'react';

interface FilterContextType {
  selectedRegion: string;
  selectedType: string;
  selectedTier: string;
  selectedInstitution: string;
  setSelectedRegion: (region: string) => void;
  setSelectedType: (type: string) => void;
  setSelectedTier: (tier: string) => void;
  setSelectedInstitution: (institution: string) => void;
  resetFilters: () => void;
  getFilterLabel: () => string;
}

const FilterContext = createContext<FilterContextType | undefined>(undefined);

export function FilterProvider({ children }: { children: ReactNode }) {
  const [selectedRegion, setSelectedRegion] = useState<string>('all');
  const [selectedType, setSelectedType] = useState<string>('all');
  const [selectedTier, setSelectedTier] = useState<string>('all');
  const [selectedInstitution, setSelectedInstitution] = useState<string>('all'); // Default to all institutions

  const resetFilters = () => {
    setSelectedRegion('all');
    setSelectedType('all');
    setSelectedTier('all');
    setSelectedInstitution('all');
  };

  const getFilterLabel = () => {
    // If specific institution is selected
    if (selectedInstitution && selectedInstitution !== 'all') {
      const institutionNames: Record<string, string> = {
        'INS-001': 'Royal Bank of Canada',
        'INS-002': 'Toronto-Dominion Bank',
        'INS-003': 'Bank of Nova Scotia',
        'INS-004': 'Bank of Montreal',
        'INS-005': 'Canadian Imperial Bank of Commerce',
        'INS-006': 'National Bank of Canada',
        'INS-007': 'Manulife Financial',
        'INS-008': 'Sun Life Financial',
        'INS-009': 'Great-West Lifeco',
        'INS-010': 'Laurentian Bank',
        'INS-011': 'Canadian Western Bank',
        'INS-012': 'Industrial Alliance',
        'INS-013': 'HSBC Bank Canada',
        'INS-014': 'Intact Financial',
        'INS-015': 'Desjardins Group',
      };
      return institutionNames[selectedInstitution] || selectedInstitution;
    }

    // Build filter label from active filters
    const filters: string[] = [];

    if (selectedRegion !== 'all') {
      filters.push(selectedRegion);
    }
    if (selectedType !== 'all') {
      filters.push(selectedType);
    }
    if (selectedTier !== 'all') {
      filters.push(selectedTier);
    }

    if (filters.length === 0) {
      return 'All Institutions';
    }

    return filters.join(' • ');
  };

  return (
    <FilterContext.Provider
      value={{
        selectedRegion,
        selectedType,
        selectedTier,
        selectedInstitution,
        setSelectedRegion,
        setSelectedType,
        setSelectedTier,
        setSelectedInstitution,
        resetFilters,
        getFilterLabel,
      }}
    >
      {children}
    </FilterContext.Provider>
  );
}

export function useFilters() {
  const context = useContext(FilterContext);
  if (context === undefined) {
    throw new Error('useFilters must be used within a FilterProvider');
  }
  return context;
}
